import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { LoggingService, WindowService } from "@merri/common";
import { Subject, merge } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import {
  AlertsService,
  AppVersionService,
  BottomSheetService,
  DialogService,
  FloatingBarOpenConfig,
  FloatingBarService,
  RoutingService,
  SeoService,
  SessionAnalyticsService,
  SessionService,
  SessionUser,
  SvgIconService,
  UtmTrackerService,
  ZendeskService,
} from "./core";
import { AccountUserSitePreferencesService } from "./features/account/services";
import { AssistantService } from "./features/assistant";
import { UnityArtifactsLoaderService } from "./features/design/shared/services";
import { PromoPartnerUserService } from "./features/promos";
import { VenueAdminService } from "./features/venue-admin";
import { VenueAdminReportsService } from "./features/venue-admin/services/venue-admin-reports.service";
import { LayoutService } from "./layout";
import { HelperMethods } from "./shared";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  initialLoad: boolean;
  showFooter: boolean;
  minPageWidth: number;
  paddingBottom: number;
  showSignUpButton: boolean;
  hideJumpTo: boolean;
  showAssistant: boolean;
  noAnimation: boolean;
  isMobileDialogOpen: boolean;
  bottomRightControlsClass: string;
  isNestedScroll: boolean;

  constructor(
    private utmTrackerService: UtmTrackerService,
    private layoutService: LayoutService,
    private sessionService: SessionService,
    private appVersionService: AppVersionService,
    private svgIconService: SvgIconService,
    private zendeskService: ZendeskService,
    private seoService: SeoService,
    private routingService: RoutingService,
    private sessionAnalyticsService: SessionAnalyticsService,
    private promoPartnerUserService: PromoPartnerUserService,
    private router: Router,
    private matDialog: MatDialog,
    private assistantService: AssistantService,
    private _loggingService: LoggingService,
    private _cdRef: ChangeDetectorRef,
    private _bottomSheetService: BottomSheetService,
    private _floatingBarService: FloatingBarService,
    private _dialogService: DialogService,
    private _windowService: WindowService,
    private _unityArtifactsLoaderService: UnityArtifactsLoaderService,
    private _accountUserSitePreferencesService: AccountUserSitePreferencesService,
    private _alertsService: AlertsService,
    private _venueAdminReportsService: VenueAdminReportsService,
    private _venueAdminService: VenueAdminService,
  ) {
    this.showFooter = true;
    this.initialLoad = true;
  }

  ngOnInit() {
    merge(this.layoutService.layoutChange$, this.sessionService.sessionUser$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          const layout = this.layoutService.currentLayout();
          const userType = this.sessionService.getUserType();

          this.showFooter = !layout.hideFooter;
          this.minPageWidth = layout.minPageWidth;

          this.showSignUpButton = layout.showSignUpButton && !userType;
          this.showAssistant = !layout.hideAssistant;
          this.isNestedScroll = layout.useNestedScroll;
        },
      });

    this.sessionService.sessionUser$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (user: SessionUser) => {
        let logObject: {
          userType?: string;
          id?: string;
          username?: string;
          status?: string;
        };

        if (!user) {
          logObject = {
            status: "Logged Out",
          };
        } else {
          logObject = {
            id: user.id.toString(),
            status: "Logged In",
            userType: user.userType,
            username: user.username,
          };
        }

        this._loggingService.logRemoteInfo("Session User Change", "AppComponent.ngOnInit", logObject);
      },
    });

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        take(1),
      )
      .subscribe(
        () => {
          this.initialLoad = false;
          LoggingService.log("AppComponent initialLoad end");
        },
        () => {
          LoggingService.log("AppComponent initialLoad error");
        },
        () => {
          LoggingService.log("AppComponent initialLoad complete");
        },
      );

    merge(
      this.matDialog.afterAllClosed,
      this.matDialog.afterOpened,
      this.assistantService.opened$,
      this.assistantService.supportAgentOpened$,
      this._dialogService.mobileChange$,
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.hideJumpTo =
            this.matDialog.openDialogs.length > 0 || this.assistantService.opened || this.assistantService.supportAgentOpened;

          this.isMobileDialogOpen = this._dialogService.openMobileDialogs.length > 0 && this._windowService.matchesMax(539);

          this._cdRef.detectChanges();
        },
      });

    this._bottomSheetService.open$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (height: number) => {
        this.paddingBottom = height;
        this._cdRef.detectChanges();
      },
    });

    this._bottomSheetService.close$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.paddingBottom = 0;
        this._cdRef.detectChanges();
      },
    });

    this._floatingBarService.open$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (config: FloatingBarOpenConfig) => {
        this.bottomRightControlsClass = `floating-bar-context-${config.name}`;
        this._cdRef.detectChanges();
        if (config.isMobile) {
          this.noAnimation = true;
          this._cdRef.detectChanges();
          this.paddingBottom = config.height;
          this._cdRef.detectChanges();
        } else {
          this.noAnimation = false;
          this.paddingBottom = null;
        }
      },
    });

    this._floatingBarService.close$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (isMobile: boolean) => {
        this.bottomRightControlsClass = null;
        this._cdRef.detectChanges();
        if (isMobile) {
          this.paddingBottom = 0;
          this._cdRef.detectChanges();
          HelperMethods.delayRun(0, () => {
            this.noAnimation = false;
          });
        }
      },
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  calculateControlsBottom: (paddingBottom: number, isMobileDialogOpen: boolean) => string = HelperMethods.memo(
    (paddingBottom: number, isMobileDialogOpen: boolean): string => {
      if (paddingBottom > 0 && !isMobileDialogOpen) {
        return `${paddingBottom + 15}px`;
      }

      return null;
    },
  );
}
